import { Box, Typography } from '@mui/material'
import { isEmpty } from 'lodash'
import { useTranslate } from 'ra-core'
import { useEffect, useMemo } from 'react'
import { CREATE, FormDataConsumer, required, SelectInput, TextInput, useGetOne } from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'

import { useGetTransactionsListForBooking } from './hooks'
import AdvancedBooleanInput from '../../components/AdvancedBooleanInput'
import AdvancedPriceNumberInput from '../../components/AdvancedPriceNumberInput'
import BasicFormToolbar from '../../components/BasicFormToolbar'
import PriceField from '../../components/PriceField'
import {
  BOOKING_TRANSACTION_CHARGE_EXTRA_MAX_AMOUNT,
  BOOKING_TRANSACTION_FORM_TYPES,
} from '../../config/bookingTransactions'
import { useCommonStyles } from '../../config/theme'
import {
  getHumanisedBookingTransactionType,
  isBookingTransactionTypeChargeExtra,
  isBookingTransactionTypeRefund,
  isBookingTransactionTypeReleasePreAuth,
} from '../../domain/bookingTransactions'
import { formatDateTime } from '../../utils/dates'
import { useSmallScreen } from '../../utils/theme'
import bookingDetailsConfig from '../bookingDetails/config'
import { FormDivider } from '../common'
import { AdvancedSimpleForm } from '../common/forms'

export const transformValues = (values) => ({
  ...values,
  charge_transaction_id: isBookingTransactionTypeRefund(values) ? values.charge_transaction_id : null,
  amount: isBookingTransactionTypeReleasePreAuth(values) ? 0 : values.amount,
})

const AmountNumberInput = ({ maxRefundableAmount, ...props }) => {
  const source = 'amount'
  const { setValue } = useFormContext()
  const values = useWatch()
  const isRefund = isBookingTransactionTypeRefund(values)

  useEffect(() => {
    if (isRefund) {
      setValue(source, maxRefundableAmount)
    } else {
      setValue(source, null)
    }
  }, [setValue, source, values.type, maxRefundableAmount, isRefund])

  return Boolean(values.type) && !isBookingTransactionTypeReleasePreAuth(values) ? (
    <AdvancedPriceNumberInput
      {...props}
      source={source}
      validate={required()}
      readOnly={isRefund && !Boolean(values.charge_transaction_id)}
      min={MIN_AMOUNT}
      max={isRefund ? maxRefundableAmount && maxRefundableAmount / 100 : BOOKING_TRANSACTION_CHARGE_EXTRA_MAX_AMOUNT}
      step="any"
      onWheel={(event) => event.target.blur()}
      onKeyDown={preventArrowStepping}
    />
  ) : null
}

const MIN_AMOUNT = 0.01

const preventArrowStepping = (event) => {
  if (['ArrowUp', 'ArrowDown'].includes(event.key)) event.preventDefault()
}

const BookingTransactionFormLayout = ({ defaultValues }) => {
  const bookingId = defaultValues?.booking_id
  const { data: bookingDetails } = useGetOne(
    bookingDetailsConfig.name,
    { id: bookingId },
    { enabled: Boolean(bookingId) },
  )
  const { data: bookingTransactions } = useGetTransactionsListForBooking(bookingId)

  const translate = useTranslate()
  const isSmallScreen = useSmallScreen()
  const commonClasses = useCommonStyles()

  const bookingTransactionTypeChoices = useMemo(
    () =>
      Object.entries(BOOKING_TRANSACTION_FORM_TYPES).map(([k, v]) => ({
        id: k,
        name: v,
        disabled: !bookingDetails?.allowed_transaction_actions.includes(k),
      })),
    [JSON.stringify(bookingDetails?.allowed_transaction_actions)], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const bookingRefundableTransactionsChoices = useMemo(
    () =>
      bookingTransactions
        ? bookingTransactions
            .filter((t) => t.refundable_amount > 0)
            .map((t) => ({
              id: t.id,
              name: `${translate(getHumanisedBookingTransactionType(t))} - ${formatDateTime(t.created_on)}`,
            }))
        : [],
    [JSON.stringify(bookingTransactions)], // eslint-disable-line react-hooks/exhaustive-deps
  )

  if (!Boolean(bookingId) || isEmpty(bookingDetails?.allowed_transaction_actions) || isEmpty(bookingTransactions)) {
    return null
  }

  return (
    <AdvancedSimpleForm defaultValues={defaultValues} toolbar={<BasicFormToolbar formType={CREATE} />} type={CREATE}>
      <SelectInput source="type" choices={bookingTransactionTypeChoices} validate={required()} />
      <FormDataConsumer>
        {({ formData }) =>
          isBookingTransactionTypeRefund(formData) && (
            <SelectInput
              source="charge_transaction_id"
              choices={bookingRefundableTransactionsChoices}
              validate={required()}
            />
          )
        }
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) => (
          <AmountNumberInput
            maxRefundableAmount={
              bookingTransactions.find((t) => t.id === formData.charge_transaction_id)?.refundable_amount
            }
          />
        )}
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) =>
          isBookingTransactionTypeReleasePreAuth(formData) && (
            <Box sx={{ marginLeft: '22px' }}>
              <Typography variant="body2" component="span">
                {translate('resources.booking-transactions.forms.releasedAmountText')}
                <PriceField record={bookingDetails} source="pa_available_amount" />
              </Typography>
            </Box>
          )
        }
      </FormDataConsumer>
      <FormDivider />
      <FormDataConsumer>
        {({ formData }) => (
          <TextInput
            source="justification"
            validate={Boolean(formData.type) && !isBookingTransactionTypeReleasePreAuth(formData) ? required() : null}
            sx={isSmallScreen ? commonClasses.commonInput : commonClasses.doubleInput}
          />
        )}
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }) =>
          isBookingTransactionTypeChargeExtra(formData) && <AdvancedBooleanInput source="handled_externally" />
        }
      </FormDataConsumer>
    </AdvancedSimpleForm>
  )
}

export default BookingTransactionFormLayout
